import { supabase } from '../../lib/supabase'
import { periodNumberFor, periodRangeLabel } from '../../lib/payPeriods'
import { getEmployeeTeamMap } from './leaveRequestQueue'

// muster.schedule_submission_state (20260922000000_schedule_submissions.sql).
export type ScheduleSubmissionState = 'submitted' | 'approved' | 'returned' | 'denied'

export type TeamScheduleSubmission = {
  id: string
  employeeId: string
  employeeName: string
  teamId: string
  teamName: string
  periodStart: string
  periodEnd: string
  periodLabel: string
  dateRange: string
  state: ScheduleSubmissionState
  sortDate: string
  submittedAt: string
  decidedAt?: string
  decisionComment?: string
  decidedByName?: string
}

type ScheduleSubmissionDecisionRow = { decision: string; comment: string | null; decided_at: string; decided_by_name: string }

type ScheduleSubmissionRow = {
  id: string
  employee_id: string
  period_start: string
  period_end: string
  state: ScheduleSubmissionState
  submitted_at: string
  decisions: ScheduleSubmissionDecisionRow[] | null
}

const SCHEDULE_SUBMISSION_COLUMNS =
  'id, employee_id, period_start, period_end, state, submitted_at, decisions(decision, comment, decided_at, decided_by_name)'

// Mirrors leaveRequestQueue.ts's own getTeamLeaveRequests exactly: every
// schedule submission a signed-in supervisor (or administrator) can see,
// real since 20260922000000_schedule_submissions.sql.
export async function getTeamScheduleSubmissions(): Promise<TeamScheduleSubmission[]> {
  const employeeTeams = await getEmployeeTeamMap()

  const { data, error } = await supabase
    .from('schedule_submissions')
    .select(SCHEDULE_SUBMISSION_COLUMNS)
    .order('period_start', { ascending: true })
  if (error || !data) return []

  return (data as unknown as ScheduleSubmissionRow[]).flatMap((row) => {
    // Not on a team the signed-in supervisor has access to: RLS itself
    // already keeps such a row out of `data`, so this only ever guards a
    // race between the two queries above, never a real gap.
    const teamInfo = employeeTeams.get(row.employee_id)
    if (!teamInfo) return []

    // A reopened, resubmitted submission can gather a second decision, the
    // same reason leaveRequestQueue.ts's own getTeamLeaveRequests reads
    // the latest by decided_at rather than assuming PostgREST's own order.
    const latest = (row.decisions ?? []).reduce<ScheduleSubmissionDecisionRow | undefined>(
      (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
      undefined,
    )
    // An approval a supervisor has since reversed no longer stands.
    const decision = latest?.decision === 'reverse' ? undefined : latest
    const periodNumber = periodNumberFor(row.period_start)

    return [
      {
        id: row.id,
        employeeId: row.employee_id,
        employeeName: teamInfo.employeeName,
        teamId: teamInfo.teamId,
        teamName: teamInfo.teamName,
        periodStart: row.period_start,
        periodEnd: row.period_end,
        periodLabel: `Pay Period ${periodNumber}`,
        dateRange: periodRangeLabel(periodNumber),
        state: row.state,
        sortDate: row.period_start,
        submittedAt: row.submitted_at,
        decidedAt: decision?.decided_at,
        decisionComment: decision?.comment ?? undefined,
        decidedByName: decision?.decided_by_name,
      },
    ]
  })
}

export type SubmittedDay = { date: string; status: string; category: string; start: string | null; end: string | null; assignment: string | null }

type SubmittedDayRow = {
  date: string
  start_time: string | null
  end_time: string | null
  status_codes: { name: string; category: string } | null
  assignments: { name: string } | null
}

// The days a submission asks the supervisor to approve: the employee's own
// schedule entries across the pay period, in date order. Null when the read
// fails, so a failure is not shown as an empty schedule.
export async function getSubmittedDays(employeeId: string, periodStart: string, periodEnd: string): Promise<SubmittedDay[] | null> {
  const { data, error } = await supabase
    .from('schedule_entries')
    .select('date, start_time, end_time, status_codes(name, category), assignments(name)')
    .eq('employee_id', employeeId)
    .gte('date', periodStart)
    .lte('date', periodEnd)
    .order('date', { ascending: true })
  if (error || !data) return null
  return (data as unknown as SubmittedDayRow[]).map((row) => ({
    date: row.date,
    status: row.status_codes?.name ?? 'No status',
    category: row.status_codes?.category ?? '',
    start: row.start_time?.slice(0, 5) ?? null,
    end: row.end_time?.slice(0, 5) ?? null,
    assignment: row.assignments?.name ?? null,
  }))
}
